import { HttpErrorResponse } from '@angular/common/http';
import { Component, Inject, OnInit } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { NzTreeNodeOptions } from 'ng-zorro-antd/tree';
import { EMPTY } from 'rxjs';
import { catchError, finalize, takeUntil, tap } from 'rxjs/operators';

import { DestroySubscriptionsService } from '@libs/src/destroy-subscription/destroy-subscription.service';
import { ToastService } from '@libs/src/toast/toast.service';
import { AccountHubsEditService } from '@main-client/src/app/account/account-hubs-edit.service';
import { UserMutationService } from '@main-client/src/app/user/user-mutation.service';

import { getServerError } from '@libs/src/error-handler/error-handler.helper';
import {
  Account,
  AccountGroupMembership,
  AccountSegmentSelection,
} from '@libs/src/models/account.model';
import { Group, GroupHiddenCustomSegment } from '@libs/src/models/group.model';
import {
  getHiddenSegmentsVisibleToUser,
  getSegmentationSelection,
  getSelectionsForSegment,
} from '@libs/src/segment-selections/segment-selections.helper';
import { SetUser } from '@libs/src/user/user.actions';
import { IAppState } from '@main-client/src/app/app.state';
import { PrivateSegmentsEditDialogData } from '@main-client/src/app/private-segments-selections/private-segments-edit-dialog/private-segments-edit-dialog-data';
import { LoadProfile } from '@main-client/src/app/profile/profile-store/profile.actions';
import { isAdminOfGroup } from '@main-client/src/app/user/user.helpers';

import get from 'lodash-es/get';
import isEqual from 'lodash-es/isEqual';
import isUndefined from 'lodash-es/isUndefined';
import reduce from 'lodash-es/reduce';

interface NzSelectOption {
  label: string;
  value: string;
}

@Component({
  providers: [DestroySubscriptionsService],
  selector: 'app-private-segments-edit-dialog',
  templateUrl: './private-segments-edit-dialog.component.html',
})
export class PrivateSegmentsEditDialogComponent implements OnInit {
  isUser: boolean;
  isUserAdmin: boolean;
  group: Group;
  segmentsSelectionsForm: UntypedFormGroup;
  segmentsToDisplay: GroupHiddenCustomSegment[];
  singleSelectNodes: { [key: string]: NzSelectOption[] } = {};
  multiSelectNodes: { [key: string]: NzTreeNodeOptions[] } = {};
  formValueBeforeSave: any;
  isFormValid = false;
  isLoading = false;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: PrivateSegmentsEditDialogData,
    private readonly accountHubsEditService: AccountHubsEditService,
    private readonly destroy$: DestroySubscriptionsService,
    private readonly dialogRef: MatDialogRef<PrivateSegmentsEditDialogComponent>,
    private readonly store: Store<IAppState>,
    private readonly toastService: ToastService,
    private readonly userMutationService: UserMutationService,
  ) {}

  ngOnInit() {
    this.group = this.data.membership?.document;
    const segmentSelections = get(
      this.data.membership,
      'hidden_segment_selections',
      [],
    );
    this.isUserAdmin =
      this.data.user.isAdmin || isAdminOfGroup(this.data.user, this.group);
    this.isUser = this.data.user?._id === this.data.account?._id;
    this.segmentsToDisplay = getHiddenSegmentsVisibleToUser(
      this.data.membership?.document,
      this.data.user,
      this.data.account,
    );
    this.segmentsSelectionsForm = new UntypedFormGroup(
      reduce(
        this.segmentsToDisplay,
        (accumulator, customSegment: GroupHiddenCustomSegment) => {
          return {
            ...accumulator,
            [customSegment._id]: new UntypedFormControl(
              getSelectionsForSegment(segmentSelections, customSegment),
            ),
          };
        },
        {},
      ),
    );
    this.formValueBeforeSave = this.segmentsSelectionsForm.getRawValue();
    this.segmentsSelectionsForm.valueChanges
      .pipe(
        takeUntil(this.destroy$),
        tap((formValue) => {
          this.isFormValid = !isEqual(formValue, this.formValueBeforeSave);
        }),
      )
      .subscribe();
    this.singleSelectNodes = this.segmentsToDisplay
      .filter(({ selection_type }) => 'single' === selection_type)
      .reduce((accumulator, customSegment: GroupHiddenCustomSegment) => {
        return {
          ...accumulator,
          [customSegment._id]: customSegment.nodes.map((node) => ({
            label: node.name,
            value: node.name,
          })),
        };
      }, {}) as { [key: string]: NzSelectOption[] };
    this.multiSelectNodes = this.segmentsToDisplay
      .filter(({ selection_type }) => 'multi' === selection_type)
      .reduce((accumulator, customSegment: GroupHiddenCustomSegment) => {
        return {
          ...accumulator,
          [customSegment._id]: customSegment.nodes.map((node) => ({
            isLeaf: true,
            key: node.name,
            title: node.name,
            value: node.name,
          })),
        };
      }, {}) as { [key: string]: NzTreeNodeOptions[] };
  }

  updateHiddenSegmentSelections(
    membership: AccountGroupMembership,
    account: Account,
    isUserAdmin: boolean,
    isUser: boolean,
  ) {
    if (!this.isFormValid) {
      return;
    }
    this.isLoading = true;
    const update = {
      hidden_segment_selections: this.generateHiddenSegmentSelectionsUpdate(
        membership,
        isUserAdmin,
      ),
    };
    const membershipUpdate$ =
      isUser && !isUserAdmin
        ? this.userMutationService.updateGroupMembership(
            membership.document,
            update,
          )
        : this.accountHubsEditService.updateMembership(
            account,
            membership,
            update,
          );
    membershipUpdate$
      .pipe(
        takeUntil(this.destroy$),
        tap((accountMemberships: AccountGroupMembership[]) => {
          this.toastService.showSuccess('Changes saved!');
          const updatedAccount = {
            ...account,
            groups: accountMemberships,
          };
          this.store.dispatch(LoadProfile(updatedAccount));
          if (isUser) {
            this.store.dispatch(SetUser(updatedAccount));
          }
          this.dialogRef.close(updatedAccount);
        }),
        catchError((response: HttpErrorResponse) => {
          this.toastService.showError(getServerError(response));
          return EMPTY;
        }),
        finalize(() => {
          this.isLoading = false;
        }),
      )
      .subscribe();
  }

  generateHiddenSegmentSelectionsUpdate(
    membership: AccountGroupMembership,
    isUserAdmin: boolean,
  ) {
    const formValues = this.segmentsSelectionsForm.getRawValue();
    const selectionUpdate = membership.document.hidden_custom_segments.map(
      (segment: GroupHiddenCustomSegment) => {
        const segmentSelectionUpdate = formValues[segment._id];
        const segmentSelection = getSegmentationSelection(
          membership.hidden_segment_selections,
          segment,
        );
        const isSegmentEditableByUser =
          segment.privacy_settings.editable_by_self || isUserAdmin;
        return !isSegmentEditableByUser
          ? segmentSelection
          : {
              ...segmentSelection,
              ...('multi' === segment.selection_type
                ? { multiselected_nodes: segmentSelectionUpdate }
                : { node: segmentSelectionUpdate }),
              custom_segment: segment._id,
            };
      },
    );
    return selectionUpdate.filter(
      (segmentSelections: AccountSegmentSelection) =>
        !isUndefined(segmentSelections),
    );
  }
}
